import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Sparkles, RefreshCw, Film, Tv } from 'lucide-react';

const GENRES = [
  { id: '', name: 'All Genres' },
  { id: '28', name: 'Action & Adventure' },
  { id: '35', name: 'Comedy' },
  { id: '27', name: 'Horror' },
  { id: '878', name: 'Sci-Fi & Fantasy' },
  { id: '16', name: 'Anime / Animation' },
  { id: '10749', name: 'Romance' },
  { id: '9648', name: 'Mystery' },
];

const ERAS = [
  { id: '', name: 'All Eras' },
  { id: '2020s', name: '2020s (New Releases)' },
  { id: '2010s', name: '2010s (Modern)' },
  { id: '2000s', name: '2000s (Classic)' },
  { id: '90s', name: '90s (Retro)' },
];

const SORTS = [
  { id: 'popularity.desc', name: 'Most Popular' },
  { id: 'vote_average.desc', name: 'Top Rated' },
  { id: 'primary_release_date.desc', name: 'Recently Added' },
];

export const SmartFinder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  // Sync state from URL search params if present
  const [mediaType, setMediaType] = useState<'movie' | 'tv'>(
    (searchParams.get('mediaType') as 'movie' | 'tv') || 'movie'
  );
  const [genre, setGenre] = useState(searchParams.get('discoverGenre') || '');
  const [era, setEra] = useState(searchParams.get('era') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'popularity.desc');

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('discover', 'true');
    params.set('mediaType', mediaType);
    if (genre) params.set('discoverGenre', genre);
    if (era) params.set('era', era);
    params.set('sort', sort);

    navigate(`/search?${params.toString()}`);
  };

  const handleReset = () => {
    setMediaType('movie');
    setGenre('');
    setEra('');
    setSort('popularity.desc');
    navigate('/');
  };

  return (
    <div id="smart-finder-container" className="max-w-7xl mx-auto px-4 md:px-12 mb-8 mt-4">
      <div className="bg-zinc-900/60 border border-white/5 rounded-2xl p-6 backdrop-blur-md shadow-2xl relative overflow-hidden group/finder">
        {/* Subtle accent light */}
        <div className="absolute top-0 left-1/4 w-96 h-1 bg-gradient-to-r from-transparent via-red-600/30 to-transparent" />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="space-y-1">
            <button 
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="flex flex-wrap items-center gap-2.5 text-white hover:text-red-400 transition-colors font-black text-xl tracking-tight cursor-pointer py-1"
              aria-expanded={isOpen}
              aria-controls="finder-filters"
            >
              <SlidersHorizontal className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-90 text-red-400' : ''}`} />
              <span>Smart Streaming Finder</span>
              <span className="text-xs bg-red-600/25 border border-red-400/40 text-red-200 px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Live Filters
              </span>
            </button>
            <p className="text-xs text-gray-300 font-medium">
              Filter by release era, user ratings, sorting order, and official genres to discover perfect titles instantly.
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="px-5 py-2.5 min-h-[40px] bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm rounded-xl border border-white/10 transition-all active:scale-95 cursor-pointer"
            >
              {isOpen ? 'Hide Options' : 'Show Options'}
            </button>
            {searchParams.get('discover') === 'true' && (
              <button
                type="button"
                onClick={handleReset}
                className="px-5 py-2.5 min-h-[40px] bg-red-600/20 hover:bg-red-600/30 text-red-300 font-bold text-sm rounded-xl border border-red-500/30 transition-all flex items-center gap-2 active:scale-95 cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Reset Filters
              </button>
            )}
          </div>
        </div>

        {isOpen && (
          <form id="finder-filters" onSubmit={handleApply} className="space-y-6 pt-4 border-t border-white/5 animate-fadeIn">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              
              {/* Media Type Selector */}
              <div className="space-y-2">
                <label className="block text-xs font-black uppercase tracking-widest text-gray-400">Content Type</label>
                <div className="grid grid-cols-2 gap-2 bg-black/40 p-1.5 rounded-xl border border-white/5">
                  <button
                    type="button"
                    onClick={() => setMediaType('movie')}
                    className={`flex items-center justify-center gap-2 py-2 rounded-lg font-black text-xs transition-all cursor-pointer ${
                      mediaType === 'movie'
                        ? 'bg-red-600 text-white shadow-lg shadow-red-600/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Film className="w-3.5 h-3.5" /> Movies
                  </button>
                  <button
                    type="button"
                    onClick={() => setMediaType('tv')}
                    className={`flex items-center justify-center gap-2 py-2 rounded-lg font-black text-xs transition-all cursor-pointer ${
                      mediaType === 'tv'
                        ? 'bg-red-600 text-white shadow-lg shadow-red-600/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Tv className="w-3.5 h-3.5" /> TV Shows
                  </button>
                </div>
              </div>

              {/* Genre Selector */}
              <div className="space-y-2">
                <label htmlFor="finder-genre" className="block text-xs font-black uppercase tracking-widest text-gray-400">Genre</label>
                <select
                  id="finder-genre"
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="w-full bg-zinc-950/80 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-red-600 transition-colors cursor-pointer"
                >
                  {GENRES.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Era Selector */}
              <div className="space-y-2">
                <label htmlFor="finder-era" className="block text-xs font-black uppercase tracking-widest text-gray-400">Release Era</label>
                <select
                  id="finder-era"
                  value={era}
                  onChange={(e) => setEra(e.target.value)}
                  className="w-full bg-zinc-950/80 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-red-600 transition-colors cursor-pointer"
                >
                  {ERAS.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort By Selector */}
              <div className="space-y-2">
                <label htmlFor="finder-sort" className="block text-xs font-black uppercase tracking-widest text-gray-400">Sort By</label>
                <select
                  id="finder-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="w-full bg-zinc-950/80 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-red-600 transition-colors cursor-pointer"
                >
                  {SORTS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleReset}
                className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm rounded-xl transition-all active:scale-95 cursor-pointer"
              >
                Reset Default
              </button>
              <button
                type="submit"
                className="px-8 py-2.5 bg-red-600 hover:bg-red-500 text-white font-black text-sm rounded-xl transition-all shadow-[0_0_20px_rgba(220,38,38,0.3)] hover:shadow-[0_0_30px_rgba(220,38,38,0.5)] flex items-center gap-2 active:scale-95 cursor-pointer"
              >
                <Sparkles className="w-4 h-4 text-white" /> Search Movies & Shows
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
